import AddIcon from "@mui/icons-material/Add";
import Can, { AbilityContext } from "../../casl/Can";
import CustomButton from "../shared/custom-button";
import DataTable from "../shared/table";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadFileButton from "../shared/download-file-button";
import ExcelFileUpload from "../shared/excel-file-upload";
import Header from "../shared/header";
import LinkButton from "../shared/link-button";
import React, { useContext, useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import useBreadcrumbStore from "../../stores/store-breadcrumbs";
import { Box, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import { getColumns } from "./clients.columns";
import { IconButton } from "@mui/material";
import { Outlet } from "react-router-dom";
import { showErrorAlert, showSuccessAlert } from "../shared/alerts";
import { useFetchClients, useUploadClientsExcel } from "../../api/clients";
import {
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import { AxiosError } from "axios";
import { ErrorTypeModel } from "../../models/error/error.model";
import useFiltersStore from "../../stores/store-filters";
import { DW_MIBI_PHASE_SIMPLE_OPTIONS } from "../../constants/mibi-phase";
import useSearchStore from "../../stores/store-query-filters";
import { TextField, InputAdornment, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
/**
 * Clients List component that displays a table of clients with filtering, sorting, and pagination capabilities.
 * 
 * @component
 * @returns {JSX.Element} A table displaying clients with filtering and pagination controls
 * 
 * @example
 * ```tsx
 * <ClientsList />
 * ```
 */
const ClientsList = () => {

const searchKey = 'clients';
const searchQuery = useSearchStore((state) => state.getSearch(searchKey));
const setSearch = useSearchStore((state) => state.setSearch);
const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(searchKey, event.target.value);
    setCurrentPage(1);
};

  const ability = useContext(AbilityContext);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  // Filter
  const filterKey = 'clients';
  const filter = useFiltersStore((state) => state.getFilterObject(filterKey));
  // Sort
  const [sort, setSort] = useState({});

  // DW Mibi phase filter state
  const [selectedDwMibiPhases, setSelectedDwMibiPhases] = useState<string[]>([]);
  const canViewDwMibiPhase = ability.can("view", "Clients By Dw Mibi Phase");

  // Enhanced filter object to include DW Mibi phase
  const enhancedFilter = {
    ...filter,
    ...(canViewDwMibiPhase && selectedDwMibiPhases.length > 0 && {
      drinking_water_mibi_phase: selectedDwMibiPhases
    })
  };

  const {
    data: clientsData,
    isLoading,
    isError,
    refetch: refetchClientsData,
  } = useFetchClients(searchQuery.trim(), currentPage, itemsPerPage, enhancedFilter, sort);

  const clients = clientsData?.data;
  const clientsTotalItems = clientsData?.pager?.total;

  // reset the breadcrumb
  const resetBreadcrumb = useBreadcrumbStore((state) => state.resetBreadcrumb);

  const handlePageChange = (newPage: GridPaginationModel) => {
    // Fetch new data from the server based on the new page
    setCurrentPage(newPage.page + 1);
    setItemsPerPage(newPage.pageSize);
  };

  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [fileKey, setFileKey] = useState(0);

  const uploadClientsExcelMutation = useUploadClientsExcel();

  const handleExcelUpload = (file: File) => {
    setSelectedFile(file);
    setSelectedFileName(file.name);
    setIsFileSelected(true);
  };

  const handleDeleteFile = () => {
    setIsFileSelected(false);
    setSelectedFile(undefined);
    setSelectedFileName(null);
    setFileKey((prevKey) => prevKey + 1); // Update the key to trigger re-render
  };

  const handleUploadExcel = async () => {
    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append("client_template", selectedFile);

        await uploadClientsExcelMutation.mutateAsync(formData);
                // Clear the selected file after successful upload
        handleDeleteFile();
                // Show success alert
        await showSuccessAlert(
          "Die Excel-Datei des Kunden wurde erfolgreich hochgeladen!"
        );
                // Refetch clients data after successful upload
        await refetchClientsData();
      }
    } catch (error) {
      const typedError = error as AxiosError<ErrorTypeModel>;

      if (typedError.response) {
        const { data } = typedError.response;
        showErrorAlert(data.message);
      } else if (typedError.request) {
                        // Handle cases where the request was made, but no response was received
        showErrorAlert("No response received from the server.");
      } else {
                        // Handle other errors
        showErrorAlert("An error occurred while processing the request.");
      }
    }
  };

  const handleSortModelChange = (sortItem: GridSortItem) => {
    setSort(sortItem);
  };

  // Handle DW Mibi phase filter change
  const handleDwMibiPhaseChange = (event: any) => {
    const value = event.target.value;
        // Check if the clear option was selected
    if (value.includes("")) {
      handleClearDwMibiPhaseFilter();
      return;
    }

    setSelectedDwMibiPhases(typeof value === 'string' ? value.split(',') : value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle DW Mibi phase filter clear
  const handleClearDwMibiPhaseFilter = () => {
    setSelectedDwMibiPhases([]);
    setCurrentPage(1);
  };

  const theme = useTheme();

  useEffect(() => {
    resetBreadcrumb();
  }, [resetBreadcrumb]);

  return (
    <>
      {!isError && (
        <Box
          height="70%"
          sx={{
            marginTop: "60px",
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", lg: "row" }}  // column on small screens, row on large
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            marginTop="60px"
            gap={2}
          >
            <Header title="Kunden" />
            <Box
              display="flex" gap={2}
              flexDirection={{ xs: "column", sm: "row" }} // stack vertically on xs, side-by-side on sm and up
              alignItems={{ xs: "stretch", sm: "center" }}  // full width on small, centered on larger
            >
              <Can I="create" a="Vdi Client">
                <LinkButton
                  label="VDI Klient erstellen"
                  startIcon={<AddIcon />}
                  to="/clients/vdi-create"
                />
              </Can>
              <Can I="create" a="Client">
                <LinkButton
                  label="Klient erstellen"
                  startIcon={<AddIcon />}
                  to="/clients/create"
                />
              </Can>
            </Box>
          </Box>

          {(ability.can("view", "All Clients") || ability.can("view", "Assigned Clients") || ability.can("view", "Iw Clients") || ability.can("view", "Tw Clients")) &&
            <Box marginTop="20px" display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                gap={2}
                mb={1}
              >
                <Box sx={{ width: { xs: "100%", md: "400px" } }}>
                  <TextField
                    fullWidth
                    placeholder="Suchen..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                        minWidth: "200px",
                        maxWidth: "300px",
                      "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent",
                      borderRadius: "20px",
                       border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                      },
                      "& .MuiOutlinedInput-input": {
                      color: "inherit",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      },
                    }}
                  />
                </Box>
                {canViewDwMibiPhase && (
                 <FormControl
                    sx={{
                      
                    width: { xs: "100%", md: "300px" },
                    "& .MuiOutlinedInput-root": {
                    backgroundColor: "transparent",
                    borderRadius: "20px",
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                    },
                    "& .MuiOutlinedInput-input": {
                    color: "inherit",
                    },
                    }}
                  >
                    <InputLabel id="dw-mibi-phase-filter-label">
                      Nach Mikrobiologischer Phase suchen
                      
                    </InputLabel>
                    <Select
                      labelId="dw-mibi-phase-filter-label"
                      id="dw-mibi-phase-filter"
                      multiple
                      value={selectedDwMibiPhases}
                      onChange={handleDwMibiPhaseChange}
                      label="Nach Mikrobiologischer Phase suchen"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const option = DW_MIBI_PHASE_SIMPLE_OPTIONS.find(opt => opt.id === value);
                            return <Chip key={value} label={option?.name || value} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {selectedDwMibiPhases.length > 0 && (
                        <MenuItem
                          value=""
                          sx={{
                            color: 'error.main',
                            fontWeight: 'bold',
                            borderBottom: '1px solid #e0e0e0',
                            marginBottom: '4px',
                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' }
                          }}
                        >
                          ✕ Filter löschen
                        </MenuItem>
                      )}
                      {DW_MIBI_PHASE_SIMPLE_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <DataTable
                  filterKey={filterKey}
                  parentPath="clients"
                  data={clients ?? []}
                  fieldsNames={getColumns(canViewDwMibiPhase)}
                  minHeight={600}
                  currentPage={currentPage - 1}
                  totalItems={clientsTotalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  ssrFilter={true}
                  ssrSort={true}
                  isLoading={isLoading}
                  onSortChange={handleSortModelChange}
                />
              </Box>
            </Box>
          }

          <Box display="flex" marginTop="20px" gap="20px" alignItems="start">
            <Can I="import" a="Clients">
              <DownloadFileButton
                label="Kundenvorlage herunterladen"
                fileName={"Client_Template.xlsx"}
                filePath="/excel/Client_Template.xlsx"
              />
              <Box display="flex">
                <ExcelFileUpload
                  key={fileKey}
                  isFileSelected={isFileSelected}
                  onFileUpload={handleExcelUpload}
                />
                {isFileSelected && (
                  <Box
                    display="flex"
                    gap="10px"
                    pl="10px"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <small>Ausgewählte Datei: {selectedFileName}</small>
                    <IconButton aria-label="delete" onClick={handleDeleteFile}>
                      <DeleteIcon />
                    </IconButton>
                    <CustomButton
                      startIcon={<SaveIcon />}
                      label="Hochladen"
                      handleOnClick={handleUploadExcel}
                    />
                  </Box>
                )}
              </Box>
            </Can>
          </Box>

          <Outlet />
        </Box>
      )}
    </>
  );
};

export default ClientsList;